import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { ref, set, get } from 'firebase/database';
import { auth, database } from '../firebase/config';
import { OSCData, ViaCEPResponse } from '../types';
import { useUserAuth } from '../contexts/UserAuthContext';
import axios from 'axios';
import PrivacyModal from './PrivacyModal';
import styled from 'styled-components';
import {
  Card,
  Button,
  Input,
  Select,
  FormGroup,
  Label,
  ErrorText,
  Title,
  Subtitle,
  Container
} from '../styles/GlobalStyles';
import { validateCNPJ, formatCNPJ } from '../utils/cnpjValidator';

const CompactContainer = styled(Container)`
  max-width: 900px;
  padding: 16px;
`;

const CompactCard = styled(Card)`
  padding: 24px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const FormSection = styled.div`
  margin-bottom: 24px;
  
  h3 {
    color: #495057;
    font-size: 16px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e9ecef;
  }
`;

const CompactFormGroup = styled(FormGroup)`
  margin-bottom: 16px;
`;

const CompactTitle = styled(Title)`
  font-size: 24px;
  margin-bottom: 8px;
`;

const CompactSubtitle = styled(Subtitle)`
  margin-bottom: 24px;
`;

const PrivacySection = styled.div`
  margin: 24px 0;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
`;

const PrivacyCheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
`;

const PrivacyCheckbox = styled.input`
  margin-top: 4px;
  transform: scale(1.2);
`;

const PrivacyLabel = styled.label`
  font-size: 14px;
  line-height: 1.5;
  color: #495057;
  cursor: pointer;
`;

const PrivacyError = styled(ErrorText)`
  margin-top: 8px;
  margin-bottom: 0;
`;

interface RegisterProps {
  onSwitchToLogin: (cnpj?: string) => void;
  prefilledCnpj?: string;
  onCnpjUsed?: () => void;
}

const Register: React.FC<RegisterProps> = ({ onSwitchToLogin, prefilledCnpj, onCnpjUsed }) => {
  const { refreshUserData } = useUserAuth();
  const [formData, setFormData] = useState<Partial<OSCData>>({});
  const [isPrefilledCnpjProcessed, setIsPrefilledCnpjProcessed] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cnpjError, setCnpjError] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyError, setPrivacyError] = useState('');

  // Função utilitária para consultar CNPJ com fallback para APIs que funcionam
  const consultarCNPJAPI = async (cnpj: string): Promise<{ razaoSocial: string; nomeInstituicao: string } | null> => {
    const numbers = cnpj.replace(/\D/g, '');
    if (numbers.length !== 14) return null;

    // Primeira tentativa: BrasilAPI (mais confiável para CORS)
    try {
      const response = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${numbers}`, {
        timeout: 10000,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (response.data && response.data.razao_social) {
        return {
          razaoSocial: response.data.razao_social,
          nomeInstituicao: response.data.nome_fantasia || response.data.razao_social
        };
      }
    } catch (error) {
      
    }

    // Segunda tentativa: API pública alternativa
    try {
      const response = await axios.get(`https://publica.cnpj.ws/cnpj/${numbers}`, {
        timeout: 10000,
        headers: {
          'Accept': 'application/json'
        }
      });
      if (response.data && response.data.razao_social) {
        return {
          razaoSocial: response.data.razao_social,
          nomeInstituicao: response.data.nome_fantasia || response.data.razao_social
        };
      }
    } catch (error) {
      
    }

    // Se todas as APIs falharam, permitir cadastro manual
    
    return null;
  };

  // Processar CNPJ pré-preenchido do Login
  useEffect(() => {
    if (prefilledCnpj && !isPrefilledCnpjProcessed) {
      setFormData(prev => ({ ...prev, cnpj: prefilledCnpj }));
      setIsPrefilledCnpjProcessed(true);
      
      // Realizar consulta automática na API
      const consultarCNPJ = async () => {
        try {
          const dadosCNPJ = await consultarCNPJAPI(prefilledCnpj);
          if (dadosCNPJ) {
            setFormData(prev => ({
              ...prev,
              cnpj: prefilledCnpj,
              razaoSocial: dadosCNPJ.razaoSocial,
              nomeInstituicao: dadosCNPJ.nomeInstituicao
            }));
          }
        } catch (error) {
    
        }
      };
      
      consultarCNPJ();
      
      // Marcar CNPJ como usado
      if (onCnpjUsed) {
        onCnpjUsed();
      }
    }
  }, [prefilledCnpj, isPrefilledCnpjProcessed, onCnpjUsed]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };



  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleCNPJChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJ(e.target.value);
    setFormData(prev => ({ ...prev, cnpj: formatted }));
    setCnpjError(''); // Limpar erro de CNPJ anterior
    setError(''); // Limpar erro geral anterior

    const numbers = formatted.replace(/\D/g, '');
    if (numbers.length === 14) {
      // Validar CNPJ antes de prosseguir
      if (!validateCNPJ(formatted)) {
        setCnpjError('CNPJ inválido. Verifique os dígitos digitados.');
        // Limpar campos preenchidos automaticamente
        setFormData(prev => ({
          ...prev,
          razaoSocial: '',
          nomeInstituicao: ''
        }));
        return;
      }
      try {
        // Verificar se o CNPJ já está cadastrado ANTES de buscar dados da API
        const oscsRef = ref(database, 'oscs');
        const snapshot = await get(oscsRef);
        
        if (snapshot.exists()) {
          const oscsData = snapshot.val();
          const cnpjExists = Object.values(oscsData).some((osc: any) => 
            osc.cnpj === formatted
          );
          
          if (cnpjExists) {
            setCnpjError('Este CNPJ já está cadastrado no sistema. Redirecionando para o login...');
            // Limpar campos preenchidos automaticamente
            setFormData(prev => ({
              ...prev,
              razaoSocial: '',
              nomeInstituicao: ''
            }));
            
            // Redirecionar para login após 2 segundos
            setTimeout(() => {
              onSwitchToLogin(formatted);
            }, 2000);
            
            return;
          }
        }

        // Se CNPJ não está duplicado, buscar dados da API
        try {
          const dadosCNPJ = await consultarCNPJAPI(formatted);
          if (dadosCNPJ) {
            setFormData(prev => ({
              ...prev,
              razaoSocial: dadosCNPJ.razaoSocial,
              nomeInstituicao: dadosCNPJ.nomeInstituicao
            }));
          }
        } catch (error) {
      
        }
      } catch (error) {
    
      }
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const formatted = formatPhone(value);
    setFormData(prev => ({ ...prev, [name]: formatted }));
  };

  const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCEP(e.target.value);
    setFormData(prev => ({ ...prev, cep: formatted }));

    const numbers = formatted.replace(/\D/g, '');
    if (numbers.length === 8) {
      setCepLoading(true);
      try {
        const response = await axios.get<ViaCEPResponse>(`https://viacep.com.br/ws/${numbers}/json/`);
        if (response.data && !response.data.erro) {
          setFormData(prev => ({
            ...prev,
            endereco: response.data.logradouro,
            bairro: response.data.bairro,
            cidade: response.data.localidade,
            uf: response.data.uf,
            pais: 'Brasil'
          }));
        }
      } catch (error) {
  
      } finally {
        setCepLoading(false);
      }
    }
  };

  const validateForm = () => {
    if (!formData.cnpj || !formData.email || !password || !confirmPassword) {
      setError('Todos os campos obrigatórios devem ser preenchidos');
      return false;
    }
    
    // Validar CNPJ
    if (!validateCNPJ(formData.cnpj)) {
      setCnpjError('CNPJ inválido. Verifique os dígitos digitados.');
      return false;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email || '')) {
      setError('Email inválido');
      return false;
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPrivacyError('');

    if (!validateForm()) {
      return;
    }

    // Validar se a política de privacidade foi aceita
    if (!privacyAccepted) {
      setPrivacyError('Para continuar é necessário autorizar');
      return;
    }

    setShowPrivacyModal(true);
  };

  const getFirebaseErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'Este email já está sendo usado por outra conta. Tente fazer login ou use outro email.';
      case 'auth/invalid-email':
        return 'Email inválido. Verifique o formato do email digitado.';
      case 'auth/operation-not-allowed':
        return 'Operação não permitida. Entre em contato com o suporte.';
      case 'auth/weak-password':
        return 'Senha muito fraca. Use pelo menos 6 caracteres com letras e números.';
      case 'auth/network-request-failed':
        return 'Erro de conexão. Verifique sua internet e tente novamente.';
      case 'auth/too-many-requests':
        return 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';
      case 'auth/user-disabled':
        return 'Esta conta foi desabilitada. Entre em contato com o suporte.';
      case 'auth/requires-recent-login':
        return 'Por segurança, faça login novamente antes de continuar.';
      default:
        return 'Erro ao criar conta. Tente novamente ou entre em contato com o suporte.';
    }
  };

  const handlePrivacyAccept = async () => {
    setLoading(true);
    setError('');
    
    try {

      
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email!, password);
      const user = userCredential.user;
      
      

      const oscData: OSCData = {
        ...formData as OSCData,
        privacyAccepted: true,
        privacyAcceptedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
      
      
      await set(ref(database, `oscs/${user.uid}`), oscData);
      
      
      // Forçar atualização dos dados do usuário no contexto
      
      await refreshUserData();
      
      
      // Usuário será redirecionado automaticamente para o Dashboard pelo AuthContext
    } catch (error: any) {

      
      let errorMessage = 'Erro inesperado ao criar conta';
      
      if (error?.code) {
        errorMessage = getFirebaseErrorMessage(error.code);
      } else if (error?.message) {
        // Para erros de rede ou outros erros sem código específico
        if (error.message.includes('network')) {
          errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Tempo limite excedido. Tente novamente.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      setLoading(false);
    } finally {
      setShowPrivacyModal(false);
    }
  };

  const handlePrivacyReject = () => {
    setShowPrivacyModal(false);
    setError('É necessário aceitar os termos de privacidade para continuar');
  };

  return (
    <CompactContainer>
      <CompactCard>
        <CompactTitle>Cadastro de OSC</CompactTitle>
        <CompactSubtitle>Preencha os dados da sua organização</CompactSubtitle>
        
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <button 
            type="button" 
            onClick={() => onSwitchToLogin()}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '0'
            }}
          >
            ← Voltar para Login
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>Dados da Organização</h3>
            <CompactFormGroup>
              <Label>CNPJ da OSC *</Label>
              <Input
                type="text"
                name="cnpj"
                value={formData.cnpj || ''}
                onChange={handleCNPJChange}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                required
                style={cnpjError ? { borderColor: '#dc3545' } : {}}
              />
              {cnpjError && <ErrorText style={{ marginTop: '8px' }}>{cnpjError}</ErrorText>}
            </CompactFormGroup>

            <FormRow>
              <CompactFormGroup>
                <Label>Razão Social</Label>
                <Input
                  type="text"
                  name="razaoSocial"
                  value={formData.razaoSocial || ''}
                  onChange={handleInputChange}
                  placeholder="Preenchido automaticamente via CNPJ"
                  readOnly
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>Nome Fantasia</Label>
                <Input
                  type="text"
                  name="nomeInstituicao"
                  value={formData.nomeInstituicao || ''}
                  onChange={handleInputChange}
                  placeholder="Preenchido automaticamente via CNPJ"
                  readOnly
                  style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                />
              </CompactFormGroup>
            </FormRow>

            <FormRow>
              <CompactFormGroup>
                <Label>E-mail da OSC *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  placeholder="contato@osc.org.br"
                  required
                />
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>WhatsApp da OSC *</Label>
                <Input
                  type="text"
                  name="whatsapp"
                  value={formData.whatsapp || ''}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  required
                />
              </CompactFormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <h3>Acesso ao Sistema</h3>
            <FormRow>
              <CompactFormGroup>
                <Label>Senha de acesso *</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                />
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>Confirmar senha *</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirme sua senha"
                  required
                />
              </CompactFormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <h3>Endereço</h3>
            <FormRow>
              <CompactFormGroup>
                <Label>CEP *</Label>
                <Input
                  type="text"
                  name="cep"
                  value={formData.cep || ''}
                  onChange={handleCEPChange}
                  placeholder="00000-000"
                  maxLength={9}
                  required
                />
                {cepLoading && <p style={{ fontSize: '12px', color: '#6c757d', margin: '4px 0 0 0' }}>Buscando endereço...</p>}
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>Endereço *</Label>
                <Input
                  type="text"
                  name="endereco"
                  value={formData.endereco || ''}
                  onChange={handleInputChange}
                  placeholder="Rua, Avenida, etc."
                  required
                />
              </CompactFormGroup>
            </FormRow>

            <FormRow>
              <CompactFormGroup>
                <Label>Número *</Label>
                <Input
                  type="text"
                  name="numero"
                  value={formData.numero || ''}
                  onChange={handleInputChange}
                  placeholder="123"
                  required
                />
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>Complemento</Label>
                <Input
                  type="text"
                  name="complemento"
                  value={formData.complemento || ''}
                  onChange={handleInputChange}
                  placeholder="Apto, Sala, etc."
                />
              </CompactFormGroup>
            </FormRow>

            <FormRow>
              <CompactFormGroup>
                <Label>Bairro *</Label>
                <Input
                  type="text"
                  name="bairro"
                  value={formData.bairro || ''}
                  onChange={handleInputChange}
                  placeholder="Nome do bairro"
                  required
                />
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>Cidade *</Label>
                <Input
                  type="text"
                  name="cidade"
                  value={formData.cidade || ''}
                  onChange={handleInputChange}
                  placeholder="Nome da cidade"
                  required
                />
              </CompactFormGroup>
            </FormRow>

            <FormRow>
              <CompactFormGroup>
                <Label>UF *</Label>
                <Select
                  name="uf"
                  value={formData.uf || ''}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Selecione o estado</option>
                  <option value="AC">Acre</option>
                  <option value="AL">Alagoas</option>
                  <option value="AP">Amapá</option>
                  <option value="AM">Amazonas</option>
                  <option value="BA">Bahia</option>
                  <option value="CE">Ceará</option>
                  <option value="DF">Distrito Federal</option>
                  <option value="ES">Espírito Santo</option>
                  <option value="GO">Goiás</option>
                  <option value="MA">Maranhão</option>
                  <option value="MT">Mato Grosso</option>
                  <option value="MS">Mato Grosso do Sul</option>
                  <option value="MG">Minas Gerais</option>
                  <option value="PA">Pará</option>
                  <option value="PB">Paraíba</option>
                  <option value="PR">Paraná</option>
                  <option value="PE">Pernambuco</option>
                  <option value="PI">Piauí</option>
                  <option value="RJ">Rio de Janeiro</option>
                  <option value="RN">Rio Grande do Norte</option>
                  <option value="RS">Rio Grande do Sul</option>
                  <option value="RO">Rondônia</option>
                  <option value="RR">Roraima</option>
                  <option value="SC">Santa Catarina</option>
                  <option value="SP">São Paulo</option>
                  <option value="SE">Sergipe</option>
                  <option value="TO">Tocantins</option>
                </Select>
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>País *</Label>
                <Input
                  type="text"
                  name="pais"
                  value={formData.pais || 'Brasil'}
                  onChange={handleInputChange}
                  required
                />
              </CompactFormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <h3>Informações Adicionais</h3>
            <FormRow>
              <CompactFormGroup>
                <Label>Site</Label>
                <Input
                  type="url"
                  name="site"
                  value={formData.site || ''}
                  onChange={handleInputChange}
                  placeholder="https://www.osc.org.br"
                />
              </CompactFormGroup>

              <CompactFormGroup>
                {/* Campo vazio para manter layout em duas colunas */}
              </CompactFormGroup>
            </FormRow>

            <FormRow>
              <CompactFormGroup>
                <Label>Instagram</Label>
                <Input
                  type="text"
                  name="instagram"
                  value={formData.instagram || ''}
                  onChange={handleInputChange}
                  placeholder="@osc_instagram"
                />
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>Categoria Prioritária *</Label>
                <Select
                  name="categoriaPrioritaria"
                  value={formData.categoriaPrioritaria || ''}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="longa_permanencia">Instituição de longa permanência</option>
                  <option value="organizacao_apoio">Instituição de organização e apoio social</option>
                  <option value="distribuicao_mediadora">Unidade de distribuição / Mediadora</option>
                </Select>
              </CompactFormGroup>
            </FormRow>
          </FormSection>

          <FormSection>
            <h3>Pessoa Responsável</h3>
            <CompactFormGroup>
              <Label>Nome completo da pessoa responsável *</Label>
              <Input
                type="text"
                name="nomeResponsavel"
                value={formData.nomeResponsavel || ''}
                onChange={handleInputChange}
                placeholder="Nome completo"
                required
              />
            </CompactFormGroup>

            <FormRow>
              <CompactFormGroup>
                <Label>WhatsApp da pessoa responsável *</Label>
                <Input
                  type="text"
                  name="whatsappResponsavel"
                  value={formData.whatsappResponsavel || ''}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  required
                />
              </CompactFormGroup>

              <CompactFormGroup>
                <Label>Cargo *</Label>
                <Input
                  type="text"
                  name="cargo"
                  value={formData.cargo || ''}
                  onChange={handleInputChange}
                  placeholder="Diretor, Coordenador, etc."
                  required
                />
              </CompactFormGroup>
            </FormRow>

            <CompactFormGroup>
              <Label>Email da pessoa responsável *</Label>
              <Input
                type="email"
                name="emailResponsavel"
                value={formData.emailResponsavel || ''}
                onChange={handleInputChange}
                placeholder="responsavel@email.com"
                required
              />
            </CompactFormGroup>
          </FormSection>

          <PrivacySection>
            <PrivacyCheckboxContainer>
              <PrivacyCheckbox
                type="checkbox"
                id="privacyAccepted"
                checked={privacyAccepted}
                onChange={(e) => {
                  setPrivacyAccepted(e.target.checked);
                  if (e.target.checked) {
                    setPrivacyError('');
                  }
                }}
              />
              <PrivacyLabel htmlFor="privacyAccepted">
                Autorizo a coleta e o tratamento do nome, cargo, telefone e e-mail dos responsáveis 
                informados neste formulário, exclusivamente para fins de contato e fortalecimento da 
                parceria com a Connecting Food. Declaro estar ciente de que os dados serão tratados 
                de forma segura e em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 
                nº 13.709/2018), e que é possível, a qualquer momento, revogar este consentimento ou 
                solicitar mais informações sobre o tratamento dos dados realizado pela Connecting Food.
              </PrivacyLabel>
            </PrivacyCheckboxContainer>
            {privacyError && <PrivacyError>{privacyError}</PrivacyError>}
          </PrivacySection>

          {error && <ErrorText>{error}</ErrorText>}

          <Button type="submit" disabled={loading}>
            {loading ? 'Cadastrando...' : 'Cadastrar OSC'}
          </Button>
        </form>
      </CompactCard>

      {showPrivacyModal && (
        <PrivacyModal
          onAccept={handlePrivacyAccept}
          onReject={handlePrivacyReject}
        />
      )}
    </CompactContainer>
  );
};

export default Register;